import { useState, useEffect } from "react";
import { db } from "../services/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import theme from "../theme";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// Convert base64 DataURL to Blob for FormData upload
const dataUrlToBlob = (dataUrl) => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

// Client-side HTML5 canvas image compression utility (~250 KB JPEG)
const compressImage = (file, maxWidth = 800, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedDataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

function normalizeShopProfile(profile) {
  if (!profile) return {};
  return {
    shopName: profile.shopName || profile.storeName || profile.businessName || "",
    ownerName: profile.ownerName || profile.proprietorName || profile.contactName || profile.name || "",
    phone: profile.phone || profile.mobile || profile.phoneNumber || "",
    address: profile.address || profile.shopAddress || profile.storeAddress || "",
    gstin: profile.gstin || profile.gst || profile.gstNumber || "",
  };
}

export default function ShopProfileForm({ onSubmitted }) {
  const { user, wholesalerId, shopProfile, getAuthToken } = useAuth();
  const initialProfile = normalizeShopProfile(shopProfile);
  const [form, setForm] = useState({
    shopName: initialProfile.shopName || "",
    ownerName: initialProfile.ownerName || "",
    phone: initialProfile.phone || "",
    address: initialProfile.address || "",
    gstin: initialProfile.gstin || "",
  });
  const [location, setLocation] = useState(shopProfile?.location || null);
  const [photos, setPhotos] = useState(shopProfile?.photoUrls || []);
  const [locating, setLocating] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (shopProfile) {
      const profile = normalizeShopProfile(shopProfile);
      setForm({
        shopName: profile.shopName || "",
        ownerName: profile.ownerName || "",
        phone: profile.phone || "",
        address: profile.address || "",
        gstin: profile.gstin || "",
      });
      if (shopProfile.location) setLocation(shopProfile.location);
      if (shopProfile.photoUrls) setPhotos(shopProfile.photoUrls);
    }
  }, [shopProfile]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (photos.length + files.length > 3) {
      setError("You can upload a maximum of 3 photos.");
      return;
    }
    setError("");
    setCompressing(true);
    try {
      const newUrls = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        // 1. Compress image locally first (~250 KB JPEG)
        const compressedDataUrl = await compressImage(file, 800, 0.75);

        // 2. Try uploading to Cloudinary via signed backend endpoint
        try {
          const token = await getAuthToken();
          const signRes = await fetch(`${BACKEND_URL}/upload/sign-retailer`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ wholesalerId: wholesalerId || shopProfile?.wholesalerId || "general", photoIndex: photos.length + i }),
          });

          if (!signRes.ok) throw new Error("Signature fetch failed");
          const { signature, timestamp, apiKey, cloudName, folder, publicId } = await signRes.json();

          const blob = dataUrlToBlob(compressedDataUrl);
          const formData = new FormData();
          formData.append("file", blob, `photo_${i}.jpg`);
          formData.append("api_key", apiKey);
          formData.append("timestamp", timestamp);
          formData.append("signature", signature);
          formData.append("folder", folder);
          formData.append("public_id", publicId);

          const uploadRes = await fetch(
            `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
            { method: "POST", body: formData }
          );

          if (!uploadRes.ok) throw new Error("Cloudinary upload failed");
          const data = await uploadRes.json();
          // Store optimized ~60-byte Cloudinary URL
          const optimisedUrl = data.secure_url.replace("/upload/", "/upload/f_auto,q_auto,w_800/");
          newUrls.push(optimisedUrl);
        } catch (cloudErr) {
          console.warn("Cloudinary upload unavailable/failed, falling back to compressed base64:", cloudErr.message);
          // Graceful fallback: if Cloudinary isn't configured (e.g. local dev), use compressed base64
          newUrls.push(compressedDataUrl);
        }
      }
      setPhotos((prev) => [...prev, ...newUrls].slice(0, 3));
    } catch (err) {
      console.error("Photo processing error:", err);
      setError("Failed to process image. Please try another photo.");
    } finally {
      setCompressing(false);
    }
  };

  const detectLocation = () => {
    if (!window.isSecureContext) {
      setError("Location requires a secure (https://) connection. Please open this site over HTTPS.");
      return;
    }
    if (!navigator.geolocation) {
      setError("Geolocation not supported on this device.");
      return;
    }
    setError("");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setLocation({ lat, lng, label: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
        setLocating(false);
      },
      (err) => {
        if (err.code === 1) {
          setError(
            "Location permission denied. On iPhone: Settings → Privacy & Security → Location Services → Safari Websites (or this app) → set to \"Allow\". Then reload the page and try again. You can skip this and continue."
          );
        } else if (err.code === 2) {
          setError("Location unavailable right now. Make sure Location Services are turned on. You can skip this.");
        } else if (err.code === 3) {
          setError("Location request timed out. Please try again or skip this step.");
        } else {
          setError("Could not get location. You can skip this.");
        }
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.shopName.trim()) return setError("Shop name is required.");
    if (!form.ownerName.trim()) return setError("Owner name is required.");
    if (!form.phone.trim() || form.phone.length < 10) return setError("Valid phone number required.");
    if (!form.address.trim()) return setError("Address is required.");

    if (form.gstin.trim()) {
      const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinRegex.test(form.gstin.trim())) {
        return setError("Invalid GSTIN format. Expected 15-digit Indian GSTIN (e.g. 20AAAAA0000A1Z5) or leave blank.");
      }
    }

    setSubmitting(true);
    try {
      await setDoc(doc(db, "shopProfiles", user.uid), {
        uid: user.uid,
        // M-4 FIX: omit "email" entirely for phone-OTP users (no email on
        // their auth token) instead of writing "". firestore.rules now
        // requires this field, if present, to equal request.auth.token.email
        // exactly — "" !== null/undefined, so writing "" would fail the
        // create rule for these users.
        ...(user.email ? { email: user.email } : {}),
        shopName: form.shopName.trim(),
        ownerName: form.ownerName.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        gstin: form.gstin.trim() || null,
        location: location || null,
        photoUrls: photos,
        approvalStatus: "pending",
        approved: false,
        orderingApproved: false,
        submittedAt: serverTimestamp(),
        status: "pending", // pending | approved | rejected
        wholesalerId: wholesalerId || shopProfile?.wholesalerId || null,  // Phase 5: required by Firestore rules (Phase 3)
      });
      onSubmitted?.();
    } catch (err) {
      console.error("Shop profile submit error:", err);
      let msg = "Failed to submit profile. Please try again.";
      if (err.code === "unavailable") {
        msg = "Network connection lost. Please check your internet connection and try again.";
      } else if (err.code === "permission-denied") {
        msg = "Permission denied. Please check your account permissions or log in again.";
      } else if (err.message) {
        msg = `Failed to save profile: ${err.message}`;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: "100dvh",
      background: theme.colors.bg,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "24px 16px 40px",
    }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        {/* Header */}
        <div style={{
          background: theme.colors.primary, color: "white",
          padding: "20px 16px", borderRadius: theme.radius.lg,
          marginBottom: 20, textAlign: "center",
        }}>
          <p style={{ fontSize: 28, margin: "0 0 6px" }}>🏪</p>
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: 20 }}>
            {shopProfile ? "Update Shop Profile" : "Register Your Shop"}
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
            {shopProfile
              ? "Update your shop details or add photos for faster verification."
              : "Fill in your shop details. Our team will review and approve your account to start ordering."}
          </p>
        </div>

        <div style={{
          background: "white", borderRadius: theme.radius.lg,
          padding: 20, boxShadow: theme.shadow?.card,
        }}>
          <label style={labelStyle}>Shop Name *</label>
          <input
            placeholder="e.g. Sharma General Store"
            value={form.shopName}
            onChange={(e) => set("shopName", e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Owner Name *</label>
          <input
            placeholder="Your full name"
            value={form.ownerName}
            onChange={(e) => set("ownerName", e.target.value)}
            style={inputStyle}
          />

          <label style={labelStyle}>Phone Number *</label>
          <input
            placeholder="10-digit mobile number"
            type="tel"
            maxLength={10}
            value={form.phone}
            onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))}
            style={inputStyle}
          />

          <label style={labelStyle}>Shop Address *</label>
          <textarea
            placeholder="Full shop address including area and city"
            rows={3}
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
          />

          <label style={labelStyle}>GSTIN <span style={{ color: theme.colors.textLight, fontWeight: 400 }}>(optional)</span></label>
          <input
            placeholder="15-digit GSTIN if available"
            value={form.gstin}
            onChange={(e) => set("gstin", e.target.value.toUpperCase())}
            maxLength={15}
            style={inputStyle}
          />

          {/* Photo Upload Section */}
          <label style={labelStyle}>
            📸 Shop & Visiting Card Photos <span style={{ color: theme.colors.textLight, fontWeight: 400 }}>(optional, up to 3)</span>
          </label>
          <div style={{
            background: "#F8FAFC", border: `1.5px dashed ${theme.colors.border}`,
            borderRadius: theme.radius.md, padding: 14, marginBottom: 16,
            textAlign: "center",
          }}>
            <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: theme.colors.accentDark }}>
              💡 Tip: Shops with real photos get verified 10x faster by distributors!
            </p>
            
            {photos.length > 0 && (
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 12 }}>
                {photos.map((url, idx) => (
                  <div key={idx} style={{ position: "relative", width: 70, height: 70, borderRadius: 8, overflow: "hidden", border: `1px solid ${theme.colors.border}` }}>
                    <img src={url} alt={`Shop ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
                      style={{
                        position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.6)",
                        color: "white", border: "none", borderRadius: "50%", width: 18, height: 18,
                        fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {photos.length < 3 && (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id="photo-upload-input"
                  onChange={handlePhotoSelect}
                  disabled={compressing || submitting}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="photo-upload-input"
                  style={{
                    display: "inline-block", padding: "8px 16px", borderRadius: theme.radius.sm,
                    background: "white", border: `1px solid ${theme.colors.border}`,
                    color: theme.colors.textDark, fontSize: 12, fontWeight: 700,
                    cursor: compressing ? "wait" : "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  {compressing ? "Compressing Photos…" : "📷 Upload Photos (Shop Front / Card)"}
                </label>
              </div>
            )}
          </div>

          {/* Location */}
          <label style={labelStyle}>Shop Location <span style={{ color: theme.colors.textLight, fontWeight: 400 }}>(optional, helps us find you)</span></label>
          {location ? (
            <div style={{
              padding: "10px 12px", borderRadius: theme.radius.md,
              background: "rgba(16,185,129,0.08)", border: `1.5px solid ${theme.colors.success}40`,
              marginBottom: 12,
            }}>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: theme.colors.success }}>
                📍 Location captured
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: theme.colors.textLight }}>
                {location.label}
              </p>
              <button
                onClick={() => setLocation(null)}
                style={{ marginTop: 6, background: "none", border: "none", color: theme.colors.danger, fontSize: 11, cursor: "pointer", padding: 0 }}
              >
                ✕ Remove
              </button>
            </div>
          ) : (
            <button
              onClick={detectLocation}
              disabled={locating}
              style={{
                width: "100%", padding: "10px", borderRadius: theme.radius.md,
                border: `1.5px solid ${theme.colors.border}`,
                background: "white", color: theme.colors.textMid,
                fontWeight: 600, fontSize: 13, cursor: "pointer",
                marginBottom: 12,
              }}
            >
              {locating ? "Detecting…" : "📍 Detect My Location"}
            </button>
          )}

          {error && (
            <p style={{ color: theme.colors.danger, fontSize: 12, fontWeight: 500, marginBottom: 10 }}>
              ⚠️ {error}
            </p>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              width: "100%", padding: "13px",
              borderRadius: theme.radius.md, border: "none",
              background: submitting ? theme.colors.disabled : theme.colors.primary,
              color: "white", fontWeight: 800, fontSize: 15,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Submitting…" : shopProfile ? "Update Profile →" : "Submit for Approval →"}
          </button>
        </div>

        <p style={{ textAlign: "center", fontSize: 11, color: theme.colors.textLight, marginTop: 12 }}>
          Once approved by our team, you'll be able to place orders.
        </p>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px",
  borderRadius: 8, border: "1.5px solid #e5e7eb",
  fontSize: 14, fontFamily: "inherit",
  marginBottom: 12, boxSizing: "border-box",
  outline: "none", background: "#f9fafb",
};

const labelStyle = {
  display: "block", fontSize: 12,
  fontWeight: 700, color: "#374151",
  marginBottom: 4,
};
